import { type FormikConfig } from 'formik';
import { memo, useCallback, useMemo } from 'react';

import { type UpdateWorkspaceInput } from '@air/api';
import { convertUnknownToError } from '@air/utils-error';

import { GeneralLibraryReadonlyDetails } from '~/components/LibraryBeta/GeneralLibrarySettingsModal/components/GeneralLibraryReadonlyDetails';
import {
  type GeneralLibraryDetailsValues,
  GeneralLibraryUpdateDetails,
} from '~/components/LibraryBeta/GeneralLibrarySettingsModal/components/GeneralLibraryUpdateDetails';
import { useGeneralLibraryMetadata } from '~/components/LibraryBeta/hooks/useGeneralLibraryMetadata';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetCustomFieldColors } from '~/swr-hooks/customFields/useGetCustomFieldColors';
import { useUpdateWorkspace } from '~/swr-hooks/workspaces/useUpdateWorkspace';
import { mapCustomFieldColorToPickerColor } from '~/utils/mapCustomFieldColorToPickerColor';
import { canChangeWorkspaceDetails } from '~/utils/permissions/workspacePermissions';

export const GeneralLibraryUpdateDetailsContainer = memo(() => {
  const { generalLibraryTitle, generalLibraryColor, generalLibraryDescription, generalLibraryIcon } =
    useGeneralLibraryMetadata();
  const { updateWorkspace } = useUpdateWorkspace();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { showLibraryToast } = useLibraryToasts();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { data: colors, isLoading: isColorsLoading } = useGetCustomFieldColors();
  const canEditWorkspace = canChangeWorkspaceDetails(permissions);

  const availableColors = useMemo(() => colors?.data.map((color) => mapCustomFieldColorToPickerColor(color)), [colors]);
  const initialValues = useMemo(
    () => ({
      title: generalLibraryTitle ?? '',
      color: {
        id: generalLibraryColor.id,
        hex: generalLibraryColor.hex,
        name: generalLibraryColor.name,
      },
      description: generalLibraryDescription ?? '',
      icon: generalLibraryIcon ?? '',
    }),
    [generalLibraryTitle, generalLibraryDescription, generalLibraryIcon, generalLibraryColor],
  );

  const onSubmit = useCallback<FormikConfig<GeneralLibraryDetailsValues>['onSubmit']>(
    (values) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const updatedValues: UpdateWorkspaceInput = {};

      if (values.color) {
        updatedValues.defaultLibraryColorId = values.color.id;
      }

      if (values.icon) {
        updatedValues.defaultLibraryIcon = values.icon;
      }

      updatedValues.defaultLibraryDescription = values.description?.length ? values.description : null;

      if (values.title) {
        updatedValues.defaultLibrary = values.title;
      }

      updateWorkspace.mutate(
        {
          workspaceId,
          ...updatedValues,
        },
        {
          onError: (error) => showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
          onSuccess: () => {
            showLibraryToast({
              message: (
                <>
                  Updated library details for <b className="ads:text-accent text-blue-6">{generalLibraryTitle}</b>.
                </>
              ),
            });
          },
        },
      );
    },
    [showLibraryToast, updateWorkspace, workspaceId, generalLibraryTitle],
  );

  if (!currentWorkspace || !generalLibraryTitle) {
    return null;
  }

  if (!canEditWorkspace) {
    return <GeneralLibraryReadonlyDetails />;
  }

  return (
    <GeneralLibraryUpdateDetails
      colors={availableColors}
      initialValues={initialValues}
      isDisabled={isColorsLoading}
      isSubmitting={updateWorkspace.isPending}
      onSubmit={onSubmit}
      workspaceName={currentWorkspace.name}
    />
  );
});

GeneralLibraryUpdateDetailsContainer.displayName = 'GeneralLibraryUpdateDetailsContainer';
